import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  type RowWithGroup,
  type TableViewAreaSelection,
  TableViewRowSelection,
  type TableViewSelection,
} from '../../selection';
import { cellDivider } from '../../styles';
import type { TableGroup } from '../group.js';
import { openDetail, popRowMenu } from '../menu.js';
import type { TableViewUILogic } from '../table-view-ui-logic.js';

export class TableRowView extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .affine-database-block-row:has(.row-select-checkbox.selected) {
      background: var(--streamlain-v2-brand-primaryTint);
    }
    .affine-database-block-row:has(.row-select-checkbox.selected)
      .row-selected-bg {
      position: relative;
    }
    .affine-database-block-row:has(.row-select-checkbox.selected)
      .row-selected-bg:before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: var(--streamlain-v2-brand-primaryTint);
    }
    .affine-database-block-row {
      width: 100%;
      display: flex;
      flex-direction: row;
      border-bottom: 1px solid ${unsafeCSS(cssVarV2.layer.insideBorder.border)};
      position: relative;
    }

    .affine-database-block-row.selected > .database-cell {
      background: transparent;
    }

    /* Zero-width positioning slot sits right after the title cell;
       the nested dv-row-ops overflows rightward into the cell, so
       the cluster reads as floating inside the title cell without
       claiming any layout width. No margin on the slot itself — any
       horizontal margin on a flex item counts toward layout even at
       width:0 and shifts every following cell. The 8 px gap from the
       cell boundary lives on .dv-row-op:last-child inside the shared
       component, where it only offsets content, not layout. */
    .row-ops {
      position: relative;
      width: 0;
      margin-top: 4px;
      height: max-content;
      visibility: hidden;
      display: flex;
      cursor: pointer;
      justify-content: end;
    }

    .affine-database-block-row .show-on-hover-row {
      visibility: hidden;
      opacity: 0;
    }
    .affine-database-block-row:hover .show-on-hover-row {
      visibility: visible;
      opacity: 1;
    }
    /* The shared checkbox component ships opacity:0 by default; make
       the row-hover the reveal trigger so every visible row-affordance
       (drag handle, checkbox, ops) stays in lockstep. */
    .affine-database-block-row:hover .row-select-checkbox {
      opacity: 1;
    }
    .affine-database-block-row:has(.active) .show-on-hover-row {
      visibility: visible;
      opacity: 1;
    }
    .affine-database-block-row:has([data-editing='true']) .show-on-hover-row {
      visibility: hidden;
      opacity: 0;
    }

    .data-view-table-view-drag-handler {
      width: 4px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: grab;
      background-color: var(--streamlain-v2-layer-background-primary);
    }
    /* Shift held — the whole row becomes a range-select target. The
       descendant selector on * is required because cells embed
       rich-text (cursor: text) and inline editors that otherwise win
       over the row-level rule, leaving the "cell" cursor visible only
       in the narrow band below the text baseline. */
    [data-shift-active] .affine-database-block-row,
    [data-shift-active] .affine-database-block-row * {
      cursor: cell;
    }
    /* Any row that is part of the active selection (even size 1) is a
       grab target anywhere on its body — clicking a selected row
       always starts a move, never deselects. Propagates into cell
       descendants for the same rich-text specificity reason. Shift
       still wins (see the rule above). */
    .affine-database-block-row[data-selected],
    .affine-database-block-row[data-selected] * {
      cursor: grab;
    }
    .affine-database-block-row[data-selected]:active,
    .affine-database-block-row[data-selected]:active * {
      cursor: grabbing;
    }
  `;

  // sl-luoo: Snapshot any selection on right-mousedown so we can restore it
  // in contextMenu. Between mousedown and contextmenu, BlockSuite's event
  // system resets the selection to a single cell/row. Standard apps (Excel,
  // Finder) preserve multi-selection on right-click — we match that behavior.
  private _savedSelection: TableViewSelection | undefined;

  private readonly _saveSelectionOnRightDown = (e: MouseEvent) => {
    if (e.button !== 2) return;
    this._savedSelection = this.selectionController?.selection;
  };

  /**
   * Convert a saved AreaSelection's row index range into a RowSelection
   * with actual row IDs, so popRowMenu can iterate over them.
   */
  private _areaToRowSelection(
    area: TableViewAreaSelection,
    currentRow: RowWithGroup
  ): TableViewRowSelection | undefined {
    const groupRows =
      area.groupKey != null
        ? this.view.groupTrait.groupDataMap$.value?.[area.groupKey]?.rows
        : this.view.getBoundedRowsWindow().rows;
    if (!groupRows) return undefined;
    const { start, end } = area.rowsSelection;
    const rows: RowWithGroup[] = [];
    for (let i = start; i <= end; i++) {
      const r = groupRows[i];
      if (r) rows.push({ id: r.rowId, groupKey: area.groupKey });
    }
    // Only use the area selection if the right-clicked row is part of it
    if (!rows.some(r => r.id === currentRow.id)) return undefined;
    return TableViewRowSelection.create({ rows });
  }

  contextMenu = (e: MouseEvent) => {
    if (this.view.readonly$.value) {
      return;
    }
    const selection = this.selectionController;
    if (!selection) {
      return;
    }
    e.preventDefault();

    const row: RowWithGroup = { id: this.rowId, groupKey: this.groupKey };
    const saved = this._savedSelection;
    this._savedSelection = undefined;

    // Try to restore the multi-selection that was active before right-click
    let restored = false;
    if (saved) {
      if (
        TableViewRowSelection.is(saved) &&
        TableViewRowSelection.includes(saved, row)
      ) {
        // Row selection: restore directly
        selection.selection = saved;
        restored = true;
      } else if (saved.selectionType === 'area') {
        // Area (cell) selection: convert row range to RowSelection
        const rowSel = this._areaToRowSelection(saved, row);
        if (rowSel) {
          selection.selection = rowSel;
          restored = true;
        }
      }
    }
    if (
      !restored &&
      !TableViewRowSelection.includes(selection.selection, row)
    ) {
      selection.selection = TableViewRowSelection.create({ rows: [row] });
    }

    const ele = e.target as HTMLElement;
    const cell = ele.closest('dv-table-view-cell-container');
    const target =
      cell ??
      (e.target as HTMLElement).closest('.database-cell') ??
      (e.target as HTMLElement);

    popRowMenu(this.tableViewLogic, popupTargetFromElement(target), selection);
  };

  setSelection = (selection?: TableViewSelection) => {
    if (this.selectionController) {
      this.selectionController.selection = selection;
    }
  };

  get groupKey() {
    return this.closest<TableGroup>('affine-data-view-table-group')?.group?.key;
  }

  get selectionController() {
    return this.tableViewLogic.selectionController;
  }

  override connectedCallback() {
    super.connectedCallback();
    // Capture row-selection before BlockSuite's event system can reset it
    this.disposables.addFromEvent(
      this,
      'mousedown',
      this._saveSelectionOnRightDown,
      true
    );
    this.disposables.addFromEvent(this, 'contextmenu', this.contextMenu);

    // Row-level modifier click — Shift / Ctrl / Meta on any part of the
    // row route selection through the shared RowSelectionController.
    // Runs in the capture phase so cell-editing click handlers don't
    // steal the gesture. A plain click is left untouched so cells keep
    // their native edit-on-click behaviour.
    this.disposables.addFromEvent(this, 'click', this._handleRowClick, true);

    // Pointer-down → drag for rows that are part of the current
    // multi-selection. Lets the user grab any selected row (not just
    // the 4 px drag handle) and drop the whole block somewhere else.
    this.disposables.addFromEvent(
      this,
      'pointerdown',
      this._handleRowPointerDown
    );

    this.classList.add('affine-database-block-row', 'database-row');
  }

  private readonly _handleRowClick = (e: MouseEvent): void => {
    if (this.view.readonly$.value) return;
    const target = e.target as HTMLElement | null;
    // The dedicated handle / checkbox handlers already route clicks
    // through the shared controller — skip here so we don't double-
    // apply (which would flip a range-add back off).
    if (target?.closest('.data-view-table-view-drag-handler')) return;
    if (target?.closest('.row-select-checkbox')) return;

    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      // Modifier click — always route through the controller for
      // range / toggle semantics.
      e.preventDefault();
      e.stopPropagation();
      this.tableViewLogic.rowSelection.select(this.rowId, {
        shift: e.shiftKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
      });
      return;
    }

    if (target?.closest('dv-table-view-cell-container')) {
      return;
    }

    const selected = this.tableViewLogic.rowSelection.selectedIds$.value;
    const inSel = selected.has(this.rowId);

    if (inSel) {
      // Plain click on a row that is part of the active selection
      // PRESERVES that selection — no collapse, no cell-edit hand-off.
      // The selection only clears when the user clicks a row that is
      // NOT part of it (handled below).
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Plain click on non-cell row chrome collapses the row selection
    // down to this single row (mirrors Gantt + Finder semantics).
    this.tableViewLogic.rowSelection.select(this.rowId, {});
  };

  private readonly _handleRowPointerDown = (e: PointerEvent): void => {
    if (this.view.readonly$.value) return;
    if (e.button !== 0 || !e.isPrimary) return;
    if (e.shiftKey || e.ctrlKey || e.metaKey) return;

    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Dedicated widgets keep their native behaviour — the drag
    // handle starts a reorder via BlockSuite's dragStart gesture,
    // the checkbox toggles selection, and active editors must keep
    // their caret.
    if (target.closest('.data-view-table-view-drag-handler')) return;
    if (target.closest('.row-select-checkbox')) return;
    if (target.closest('input, textarea, [contenteditable="true"]')) return;

    const selected = this.tableViewLogic.rowSelection.selectedIds$.value;
    if (!selected.has(this.rowId)) return;

    // Row is part of the active selection — pointerdown ALWAYS starts
    // the drag gesture. We stop propagation so BlockSuite's area-select
    // handler doesn't fire on the underlying cell (which would replace
    // the row selection with an area selection and deselect the set).
    e.stopPropagation();
    try {
      this.setPointerCapture(e.pointerId);
    } catch {
      // capture may be unavailable when the element detaches — non-fatal
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    let armed = true;

    const onMove = (move: PointerEvent): void => {
      if (!armed) return;
      if (move.pointerId !== pointerId) return;
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      if (Math.hypot(dx, dy) < 4) return;
      armed = false;
      cleanup();
      try {
        this.releasePointerCapture(pointerId);
      } catch {
        // non-fatal — reorder will install its own capture below
      }
      this.tableViewLogic.startRowReorder(this.rowId, move, this);
    };

    const onUp = (up: PointerEvent): void => {
      if (up.pointerId !== pointerId) return;
      armed = false;
      cleanup();
      try {
        this.releasePointerCapture(pointerId);
      } catch {
        // already released — non-fatal
      }
    };

    const cleanup = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  protected override render(): unknown {
    const view = this.view;
    // Live-preview row reorder: the shared controller publishes a
    // `{rowId → {dx, dy}}` map, rebuilt on every state change from a
    // pointer-down row-rect snapshot. Reading it here lets the row
    // itself be the preview — no floating clone, no second layer.
    // Transition stays on `none` when the map is empty so the commit
    // frame (new row order + cleared shifts) snaps without a second
    // animation.
    const shifts = this.tableViewLogic.reorder.liveShifts$.value;
    const offset = shifts.get(this.rowId);
    const dy = offset?.dy ?? 0;
    const reorderBusy = shifts.size > 0;
    const transformStyle = dy !== 0 ? `translateY(${dy}px)` : 'none';
    const transitionStyle = reorderBusy ? 'transform 120ms ease-out' : 'none';
    this.style.setProperty('transform', transformStyle);
    this.style.setProperty('transition', transitionStyle);
    this.style.setProperty('will-change', 'transform');
    // Flag any row that is part of the active selection so the grab
    // cursor + pointerdown-drag gate kick in. Holds for single-select
    // too — clicking a selected row always starts a move, never edits.
    const selected = this.tableViewLogic.rowSelection.selectedIds$.value;
    this.toggleAttribute('data-selected', selected.has(this.rowId));
    return html`
      ${view.readonly$.value
        ? nothing
        : html`<div class="data-view-table-left-bar" style="height: 34px">
            <div style="display: flex;">
              <dv-row-drag-handle
                class="data-view-table-view-drag-handler show-on-hover-row row-selected-bg"
                .onSelect=${(mods: {
                  shift?: boolean;
                  ctrl?: boolean;
                  meta?: boolean;
                }) => this.tableViewLogic.rowSelection.select(this.rowId, mods)}
              ></dv-row-drag-handle>
              ${(() => {
                const sel = this.tableViewLogic.selection$.value;
                const selected =
                  sel?.selectionType === 'row' &&
                  TableViewRowSelection.includes(sel, {
                    id: this.rowId,
                    groupKey: this.groupKey,
                  });
                return html`<dv-row-select-checkbox
                  .selected=${selected}
                  .onToggle=${(mods: {
                    shift?: boolean;
                    ctrl?: boolean;
                    meta?: boolean;
                  }) =>
                    this.tableViewLogic.rowSelection.select(this.rowId, mods)}
                ></dv-row-select-checkbox>`;
              })()}
            </div>
          </div>`}
      ${repeat(
        view.properties$.value,
        v => v.id,
        (column, i) => {
          const clickDetail = () => {
            if (!this.selectionController) {
              return;
            }
            this.setSelection(
              TableViewRowSelection.create({
                rows: [{ id: this.rowId, groupKey: this.groupKey }],
              })
            );
            openDetail(
              this.tableViewLogic,
              this.rowId,
              this.selectionController
            );
          };
          const openMenu = (e: MouseEvent) => {
            if (!this.selectionController) {
              return;
            }
            const ele = e.currentTarget as HTMLElement;
            const selection = this.selectionController.selection;
            if (
              !TableViewRowSelection.is(selection) ||
              !selection.rows.some(
                row => row.id === this.rowId && row.groupKey === this.groupKey
              )
            ) {
              const row = { id: this.rowId, groupKey: this.groupKey };
              this.setSelection(
                TableViewRowSelection.create({
                  rows: [row],
                })
              );
            }
            popRowMenu(
              this.tableViewLogic,
              popupTargetFromElement(ele),
              this.selectionController
            );
          };
          const cellFrameStyle = {
            alignSelf: 'stretch',
            display: 'flex',
            position: 'relative',
            width: `${column.width$.value}px`,
          };
          return html`
            <div style="display: flex; align-self: stretch;">
              <div
                class="database-cell-frame"
                data-database-cell-frame="true"
                data-row-id="${this.rowId}"
                data-row-index="${this.rowIndex}"
                data-column-id="${column.id}"
                data-column-name="${column.name$.value}"
                data-column-index="${i}"
                style=${styleMap(cellFrameStyle)}
              >
                <dv-table-view-cell-container
                  class="database-cell"
                  style=${styleMap({
                    width: '100%',
                    height: '100%',
                    border: i === 0 ? 'none' : undefined,
                  })}
                  .tableViewLogic="${this.tableViewLogic}"
                  .column="${column}"
                  .rowId="${this.rowId}"
                  data-row-id="${this.rowId}"
                  .rowIndex="${this.rowIndex}"
                  data-row-index="${this.rowIndex}"
                  .columnId="${column.id}"
                  data-column-id="${column.id}"
                  data-column-name="${column.name$.value}"
                  .columnIndex="${i}"
                  data-column-index="${i}"
                >
                </dv-table-view-cell-container>
              </div>
              <div class="${cellDivider}"></div>
            </div>
            ${!column.readonly$.value &&
            column.view.mainProperties$.value.titleColumn === column.id
              ? html`<dv-row-ops
                  class="row-ops show-on-hover-row"
                  .onPeek=${() => clickDetail()}
                  .onMore=${!view.readonly$.value
                    ? (target: HTMLElement) => {
                        const fakeEvent = {
                          currentTarget: target,
                        } as unknown as MouseEvent;
                        openMenu(fakeEvent);
                      }
                    : undefined}
                ></dv-row-ops>`
              : nothing}
          `;
        }
      )}
      <div class="database-cell add-column-button"></div>
    `;
  }

  @property({ attribute: false })
  accessor tableViewLogic!: TableViewUILogic;

  @property({ attribute: false })
  accessor rowId!: string;

  @property({ attribute: false })
  accessor rowIndex!: number;

  get view() {
    return this.tableViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-table-row': TableRowView;
  }
}
